import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi! I'm here to help with questions about our AWS & OCI courses. Ask me about the syllabus, schedule, trainer, or pricing!" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMsg = input.trim();
    setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chatbot`, { message: userMsg });
      setMessages((prev) => [...prev, { role: "bot", text: res.data.reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: "bot", text: "Sorry, I couldn't process that. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 right-6 z-50 w-14 h-14 bg-[#E32636] hover:bg-[#FF2D3B] text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-105 glow-red"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {isOpen && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-36 right-6 z-50 w-80 sm:w-96 h-[28rem] bg-[#16161F]/98 backdrop-blur-xl border border-[#27272A] rounded-md shadow-2xl flex flex-col chat-animate-in"
        >
          <div className="px-4 py-3 border-b border-[#27272A] bg-[#111118] rounded-t-md">
            <h4 className="font-['Space_Grotesk'] font-semibold text-[#F4F4F5] text-sm">Course Assistant</h4>
            <p className="text-xs text-[#71717A]">Ask about our AWS & OCI Courses</p>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-md text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-[#E32636] text-white"
                      : "bg-[#0A0A0F] border border-[#27272A] text-[#A1A1AA]"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#0A0A0F] border border-[#27272A] px-3 py-2 rounded-md text-sm text-[#71717A]">
                  Typing...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="p-3 border-t border-[#27272A] flex gap-2">
            <input
              data-testid="chatbot-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              className="flex-1 px-3 py-2 bg-[#0A0A0F] border border-[#27272A] rounded-md text-sm text-[#F4F4F5] placeholder-[#71717A] focus:outline-none focus:ring-1 focus:ring-[#E32636]"
            />
            <button
              type="submit"
              data-testid="chatbot-send-button"
              disabled={loading || !input.trim()}
              className="w-9 h-9 flex items-center justify-center bg-[#E32636] hover:bg-[#FF2D3B] text-white rounded-md disabled:opacity-50 transition-colors"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
